import axios, { Canceler } from 'axios'
import { axiosInstance } from './http-common.service'
import { HttpService } from './http.service'
import { EnhancedWithAuthHttpService } from './http-auth.service'

import {
  ICancelRemovalDto,
  IChangeUserPasswordDto,
  IMessageResponseDto,
  IProfileUserUpdateDto,
  IProfileUserResponse,
  ISetUserTimezoneDto,
  IUpdateUserRolesDto,
  IChangeUserPasswordRespDto,
  IProfileUsersResponse,
  IUserSuspensionDto,
  IUserExposePrivateKeyRespDto,
  IUserPrivateKeyVerifyDto,
  IUser2FASwitchRespDto,
  IUpdateUserDefaultThemeDto,
  IAddressAndWallet,
  IUserWalletTotalBallances,
  EUserProviders,
  IUserProviderResponse,
  IUserRefferalResponse,
} from 'src/types'

class UserService extends EnhancedWithAuthHttpService {
  CancelToken
  cancel?: Canceler

  constructor() {
    super(new HttpService(axiosInstance))
    this.CancelToken = axios.CancelToken
  }

  async updateUser(dto: IProfileUserUpdateDto) {
    return await this.put<IMessageResponseDto, IProfileUserUpdateDto>('users', dto)
  }

  hideUserWelcome() {
    return this.put<undefined, null>('users/welcome-tab', null)
  }

  changePassword(dto: IChangeUserPasswordDto) {
    return this.put<IChangeUserPasswordRespDto, IChangeUserPasswordDto>('users/password', dto)
  }

  verifyChangePassword(token: string, code: string) {
    return this.put<IMessageResponseDto, { code: string }>(`users/password/verify/${token}`, {
      code,
    })
  }

  getMe(userId?: string) {
    const a = this.get<IProfileUserResponse>('users', {
      params: { userId },
    })
    return a
  }

  getUsers(params: any) {
    if (this.cancel) {
      this.cancel()
    }

    return this.get<IProfileUsersResponse>('users/all', {
      params,
      cancelToken: new this.CancelToken((c) => {
        this.cancel = c
      }),
    })
  }

  removeMe() {
    return this.put<IMessageResponseDto, {}>('users/current', {})
  }

  async removeUsers(ids: string[]) {
    return await this.delete<string>(`users`, { data: { ids } })
  }

  cancelRemove(params: ICancelRemovalDto) {
    return this.get<IMessageResponseDto>('users/cancel-deletion', { params })
  }

  updateUserRoles(dto: IUpdateUserRolesDto) {
    return this.put<IMessageResponseDto, Pick<IUpdateUserRolesDto, 'roles'>>(
      `users/${dto.id}/roles`,
      {
        roles: dto.roles,
      },
    )
  }

  setUserWlp(data: object) {
    return this.put<IMessageResponseDto, any>(`users/assign-white-label`, {
      data,
    })
  }

  setLanguage(languageId: string) {
    return this.put<IMessageResponseDto, { languageId: string }>(`users/language`, {
      languageId,
    })
  }

  setTimezone(dto: ISetUserTimezoneDto) {
    return this.put<IMessageResponseDto, ISetUserTimezoneDto>(`users/timezone`, dto)
  }

  setDefaultDarkMode(dto: IUpdateUserDefaultThemeDto) {
    return this.put<IMessageResponseDto, IUpdateUserDefaultThemeDto>(`users/dark-mode/default`, dto)
  }

  setDarkMode(darkMode: boolean) {
    return this.put<IMessageResponseDto, { darkMode: boolean }>(`users/dark-mode`, { darkMode })
  }

  updateUserSuspension(dto: IUserSuspensionDto) {
    return this.put<IMessageResponseDto, IUserSuspensionDto>(`users/suspension`, dto)
  }

  requestPrivateKey() {
    return this.put<IMessageResponseDto, {}>(`users/wallets/private-key`, {})
  }

  retrievePrivateKey(code: string) {
    return this.put<IUserExposePrivateKeyRespDto, IUserPrivateKeyVerifyDto>(
      `users/wallets/private-key/verify`,
      { code },
    )
  }

  fetchAddressAndBalance() {
    return this.get<IAddressAndWallet>(`users/admin/addr-and-balance`)
  }

  send2FAEmailVerification() {
    return this.put<IMessageResponseDto, null>(`users/2fa/email/code`, null)
  }

  enable2FAEmail(code: string) {
    return this.put<IMessageResponseDto, { code: string }>(`users/2fa/email/enable`, { code })
  }

  create2FATotpUrl() {
    return this.post<string, null>(`users/2fa/totp/url`, null)
  }

  enable2FATotp(code: string) {
    return this.put<IMessageResponseDto, { code: string }>(`users/2fa/totp/enable`, { code })
  }

  disable2FA(code: string) {
    return this.put<IMessageResponseDto, { code: string }>(`users/2fa/disable`, { code })
  }

  removeUser2FAStatus(userId: string) {
    return this.put<IMessageResponseDto, { userId: string }>(`users/admin/2fa/disable`, { userId })
  }

  request2FAEmailDisable() {
    return this.put<IMessageResponseDto, null>(`users/2fa/email/disable`, null)
  }

  get2FATotpUri(code: string) {
    return this.put<string, { code: string }>(`users/2fa/totp/uri`, { code })
  }

  switch2FAMethod(code: string) {
    return this.put<IUser2FASwitchRespDto, { code: string }>(`users/2fa/switch`, { code })
  }

  verify2FAToTotpSwitch(code: string) {
    return this.put<IMessageResponseDto, { code: string }>(`users/2fa/switch/totp/verify`, {
      code,
    })
  }

  getTotalUserWalletBalances() {
    return this.get<IUserWalletTotalBallances>(`user-wallet/total-balance`)
  }

  makeCustodialWalletActive(wallet_id: string) {
    return this.put<IMessageResponseDto, { wallet_id: string }>(`user-wallet/custodial-default`, {
      wallet_id,
    })
  }

  disconnectUserSelfManagedWallet(wallet_id: string) {
    return this.delete<IMessageResponseDto>(`user-wallet/`, { data: { id: wallet_id } })
  }

  getUserProviders() {
    return this.get<IUserProviderResponse[]>(`users/providers`)
  }

  getUserReferrals(params?: {}) {
    return this.get<IUserRefferalResponse[]>(`users/referrals`, { params })
  }

  removeUserProvider(provider: EUserProviders) {
    return this.delete<IMessageResponseDto>(`users/providers`, { data: { provider } })
  }
}

export const userService = new UserService()

export default UserService
